import { Alert, Button, Container, LinearProgress, Stack } from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import { ToggleDrawerContext } from "../../../../contexts/ToggleDrawer";
import UserProvider from "../../../../contexts/UserProvider";
import { MUI_BUTTON_NEGATIVE } from "../../../../css/MuiClasses";
import useUser from "../../../../hooks/useUser";
import {
  isEmptyString,
  isObjectEmpty,
  navigateToPhoneSetup,
} from "../../../../utils/Functions";
import { cancelButtonVariant } from "../../../buttons/ConfirmAndCancelButtons";
import ClinicPickerDialog from "../../../dialogs/ClinicPickerDialog";
import SimpleAlert from "../../../display/SimpleAlert";
import {
  AppointmentType,
  BookingContext,
} from "../context/BookingModalContext";
import AdminBooking from "./AdminBooking";
import UserBooking from "./UserBooking";

export default function BookingModal({ selectedDate, typeOfAppointement }) {
  const user = useUser();
  const data = React.useContext(BookingContext);
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const [isOpen, setIsOpen] = React.useContext(ToggleDrawerContext);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();
  const methods = useForm({
    defaultValues: {
      startDate: selectedDate ? dayjs(selectedDate) : null,
    },
  });

  React.useEffect(() => {
    if (typeOfAppointement && !isObjectEmpty(typeOfAppointement)) {
      const currentLangName = typeOfAppointement.translations[currentLang];
      const defaultValue = currentLangName
        ? currentLangName
        : typeOfAppointement.title;
      methods.setValue(AppointmentType, {
        id: typeOfAppointement.id,
        title: defaultValue,
      });
    }
  }, []);

  if (user.isLoading) {
    return <LinearProgress />;
  } else if (user.error) {
    return (
      <SimpleAlert
        message={"ErrorFetchingBookngModalData"}
        severity={"error"}
        testId={"appointment.booking-form.load-error-alert"}
      />
    );
  }

  if (!user?.currentClinic?.id) {
    return <ClinicPickerDialog />;
  }

  if (isEmptyString(user.profile?.phoneNumber)) {
    return (
      <Stack alignItems={"center"} m={5} spacing={2}>
        <Alert severity="warning" sx={{ color: "white" }} variant="filled">
          {t("CantBookAppointmentBeforeAddingAPhoneNumber")}
        </Alert>
        <Button
          color="info"
          onClick={() => {
            setIsOpen(false);
            navigateToPhoneSetup(navigate);
          }}
          variant="contained"
          data-testid={"appointment.booking-form.add-phone-button"}
        >
          {t("AddPhoneNumber")}
        </Button>
        <Button
          color="error"
          size="medium"
          onClick={() => setIsOpen(false)}
          className={MUI_BUTTON_NEGATIVE}
          variant={cancelButtonVariant}
          type="button"
          sx={{ mt: 22 }}
          data-testid={"appointment.booking-form.phone-missing-cancel-button"}
        >
          {t("Cancel")}
        </Button>
      </Stack>
    );
  }

  if (user.booleans.reachedPendingLimit) {
    return (
      <Container>
        <SimpleAlert
          severity={"warning"}
          message={"YouHaveReachedThePendingAppointmentLimitCount"}
          testId={"appointment.booking-form.pending-limit-alert"}
        />
        <Stack
          justifyContent={"end"}
          direction={"row"}
          spacing={1}
          display="flex"
          alignItems="center"
          sx={{
            flexWrap: "wrap",
            rowGap: 1,
            columnGap: 2,
            width: "100%",
            mt: 2,
          }}
        >
          <Button
            color="error"
            size="medium"
            onClick={() => setIsOpen(false)}
            className={MUI_BUTTON_NEGATIVE}
            variant={cancelButtonVariant}
            type="button"
            data-testid={"appointment.booking-form.pending-limit-cancel-button"}
          >
            {t("Cancel")}
          </Button>
        </Stack>
      </Container>
    );
  }

  if (user.roles.adminOrEmployee) {
    return (
      <UserProvider>
        <FormProvider {...methods} key={"admin-form-provider"}>
          <AdminBooking />
        </FormProvider>
      </UserProvider>
    );
  } else {
    return (
      <FormProvider {...methods} key={"user-form-provider"}>
        <UserBooking />
      </FormProvider>
    );
  }
}
